import React, { useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Rocket, Loader2, DollarSign, Coins, FileText,
  AlertTriangle, Target, CheckCircle2, XCircle, Signal, ChevronDown, ChevronUp,
  ExternalLink, Info, Microscope, HelpCircle, RefreshCw, Share2,
} from "lucide-react";
import { APP } from "@/constants/testIds";

function SectionHeader({ icon: Icon, label, sublabel }) {
  return (
    <div className="flex items-center gap-3 border-b border-[color:var(--fg)] pb-2 mb-4">
      <Icon size={18} strokeWidth={2} />
      <div className="flex-1">
        <div className="font-display text-xl uppercase tracking-tight">{label}</div>
        {sublabel && <div className="text-[10px] font-mono-tight uppercase tracking-widest text-[color:var(--muted)]">{sublabel}</div>}
      </div>
    </div>
  );
}

function MetricCell({ label, value, big }) {
  return (
    <div className="border border-[color:var(--line)] p-3 min-w-0">
      <div className="text-[10px] font-mono-tight uppercase tracking-widest text-[color:var(--muted)]">{label}</div>
      <div className={`font-semibold mt-1 break-words ${big ? "font-display text-xl sm:text-2xl md:text-3xl leading-tight" : "text-sm"}`}>{value}</div>
    </div>
  );
}

function ScoreBar({ score }) {
  const s = Math.max(0, Math.min(100, score));
  return (
    <div className="mt-2">
      <div className="h-2 bg-[color:var(--line)] relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-[color:var(--hot)]"
          style={{ width: `${s}%` }}
        />
      </div>
      <div className="flex justify-between text-[10px] font-mono-tight text-[color:var(--muted)] mt-1">
        <span>0</span><span>{s}/100</span><span>100</span>
      </div>
    </div>
  );
}

// ---------- Standardized confidence label ----------
// Three labels only: MEASURED (accent solid), ESTIMATED (accent outline), AI ASSESSMENT (muted outline)
function ConfidenceBadge({ confidence, testId }) {
  const label = (confidence || "ai_assessment").toLowerCase();
  const map = {
    measured: {
      text: "MEASURED",
      className: "bg-[color:var(--accent)] text-[color:var(--bg)]",
      title: "Directly sourced from a real, verifiable data source",
    },
    estimated: {
      text: "ESTIMATED",
      className: "border border-[color:var(--accent)] text-[color:var(--accent)]",
      title: "Calculation or inference from data — arithmetic shown",
    },
    ai_assessment: {
      text: "AI ASSESSMENT",
      className: "border border-[color:var(--muted)] text-[color:var(--muted)]",
      title: "Qualitative LLM judgment — no direct measurement or arithmetic",
    },
  };
  const cfg = map[label] || map.ai_assessment;
  return (
    <span
      className={`text-[9px] font-mono-tight uppercase tracking-widest px-1.5 py-0.5 flex-shrink-0 ${cfg.className}`}
      title={cfg.title}
      data-testid={testId}
    >
      {cfg.text}
    </span>
  );
}


function ScoreBreakdown({ factors }) {
  const [open, setOpen] = useState(true);
  if (!factors || factors.length === 0) return null;
  const totalWeight = factors.reduce((sum, f) => sum + (f.weight || 0), 0) || 100;
  const measuredCount = factors.filter((f) => (f.confidence || (f.is_measured ? "measured" : "ai_assessment")) === "measured").length;
  return (
    <div className="mt-3 border border-[color:var(--fg)]" data-testid="score-breakdown">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="score-breakdown-toggle"
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-mono-tight uppercase tracking-widest hover:bg-[color:var(--fg)] hover:text-[color:var(--bg)] transition-colors"
      >
        <span>How this score is calculated</span>
        <span className="flex items-center gap-1">
          {open ? <>Hide {factors.length} factors <ChevronUp size={12} /></> : <>Show {factors.length} factors <ChevronDown size={12} /></>}
        </span>
      </button>
      {open && (
        <div className="border-t border-[color:var(--fg)] divide-y divide-[color:var(--line)]">
          {factors.map((f, i) => {
            const weightPct = totalWeight ? Math.round((f.weight / totalWeight) * 100) : 0;
            const score = Math.max(0, Math.min(100, f.score || 0));
            return (
              <div key={`sb-${i}-${f.name}`} className="p-3" data-testid={`factor-${i}`}>
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <div className="text-sm font-semibold truncate flex items-center gap-2 min-w-0">
                    <span className="truncate">{f.name}</span>
                    <ConfidenceBadge
                      confidence={f.confidence || (f.is_measured ? "measured" : "ai_assessment")}
                      testId={`factor-${i}-confidence`}
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] font-mono-tight text-[color:var(--muted)]">{weightPct}% weight</span>
                    <span className="font-mono-tight text-xs bg-[color:var(--fg)] text-[color:var(--bg)] px-2 py-0.5">
                      {score}/100
                    </span>
                  </div>
                </div>
                <div className="h-1 bg-[color:var(--line)] mb-2 relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-[color:var(--fg)]" style={{ width: `${score}%` }} />
                </div>
                <div className="text-xs text-[color:var(--muted)] leading-snug">{f.signal}</div>
                {f.evidence && (
                  <div className="mt-2 flex items-start gap-2 text-xs">
                    <span className="text-[color:var(--accent)] font-mono-tight uppercase tracking-widest text-[9px] mt-0.5 flex-shrink-0">
                      Evidence:
                    </span>
                    <span className="flex-1 leading-snug">{f.evidence}</span>
                    {f.evidence_url && (
                      <a
                        href={f.evidence_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`factor-${i}-source`}
                        className="text-[color:var(--fg)] hover:text-[color:var(--accent)] flex items-center gap-0.5 flex-shrink-0"
                        title="View source"
                      >
                        <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div className="p-2 text-[10px] font-mono-tight uppercase tracking-widest text-[color:var(--muted)] text-right">
            {measuredCount}/{factors.length} measured · weights {totalWeight}% (normalized to 100%)
          </div>        </div>
      )}
    </div>
  );
}

function MarketDemandSection({ md }) {
  return (
    <div>
      <SectionHeader icon={Signal} label="AI Demand Estimate" sublabel="MEASURED · ESTIMATED · AI ASSESSMENT" />
      <div className="mb-4">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div className="text-xs uppercase tracking-widest text-[color:var(--muted)]">Overall estimate</div>
          <span
            className="text-[9px] font-mono-tight uppercase tracking-widest border border-[color:var(--muted)] text-[color:var(--muted)] px-2 py-0.5"
            title="Weighted average — see individual factor confidence labels below"
            data-testid="ai-estimate-badge"
          >
            AI-analyzed · see breakdown
          </span>
        </div>
        <div className="font-display text-6xl leading-none mt-2" data-testid="demand-score">
          {md.demand_score}<span className="text-2xl text-[color:var(--muted)]">/100</span>
        </div>
        <ScoreBar score={md.demand_score} />
        <ScoreBreakdown factors={md.score_breakdown} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        <MetricCell label="Growth (qualitative)" value={md.growth_rate} />
        <MetricCell label="Competition" value={md.competition_level} />
        <MetricCell label="Search trend" value={md.search_trend} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)] mb-2">Key signals</div>
        <ul className="space-y-1.5 text-sm" data-testid="key-signals-list">
          {md.key_signals.map((s, i) => (
            <li key={`sig-${i}`} className="flex gap-2">
              <span className="text-[color:var(--hot)] mt-1">▸</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>
      {md.methodology && (
        <div
          className="mt-6 border border-[color:var(--line)] bg-[color:var(--line)]/30 p-3 flex gap-3 text-xs leading-relaxed"
          data-testid="methodology-disclosure"
        >
          <Info size={14} className="flex-shrink-0 mt-0.5 text-[color:var(--muted)]" />
          <div>
            <div className="font-mono-tight uppercase tracking-widest text-[10px] text-[color:var(--muted)] mb-1">
              Methodology
            </div>
            <div className="text-[color:var(--muted)]">{md.methodology}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function CostSection({ cost }) {
  return (
    <div>
      <SectionHeader icon={DollarSign} label="Startup Cost" sublabel="what you'll actually spend" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
        <MetricCell label="Initial total" value={cost.total_range} big />
        <MetricCell label="Monthly burn" value={cost.monthly_burn} />
        <MetricCell label="To profit" value={cost.path_to_profitability} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)] mb-2">Line items</div>
        <div className="border border-[color:var(--fg)]">
          {cost.line_items.map((li, i) => (
            <div key={`li-${i}`} className="grid grid-cols-12 gap-3 border-b border-[color:var(--line)] last:border-b-0 p-3 text-sm">
              <div className="col-span-4 font-semibold">{li.category}</div>
              <div className="col-span-3 font-mono-tight">{li.range}</div>
              <div className="col-span-5 text-[color:var(--muted)]">{li.notes}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MonetizationSection({ models }) {
  return (
    <div>
      <SectionHeader icon={Coins} label="Monetization Models" sublabel="how to make money · math shown" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="monetization-models">
        {models.map((m, i) => (
          <div key={`mm-${i}`} className="border border-[color:var(--fg)] p-4" data-testid={`monetization-model-${i}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="font-display text-lg leading-tight">{m.name}</div>
              <span className="tag text-[9px] !py-0.5 !px-1.5">${i + 1}</span>
            </div>
            <div className="font-mono-tight text-sm mb-3">{m.pricing}</div>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">Pros</div>
            <ul className="mb-3 mt-1 space-y-1 text-xs">
              {m.pros.map((p, j) => (
                <li key={`p-${j}`} className="flex gap-2">
                  <CheckCircle2 size={12} className="text-[color:var(--accent)] flex-shrink-0 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">Cons</div>
            <ul className="mt-1 mb-3 space-y-1 text-xs">
              {m.cons.map((c, j) => (
                <li key={`c-${j}`} className="flex gap-2">
                  <XCircle size={12} className="text-[color:var(--hot)] flex-shrink-0 mt-0.5" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <div className="pt-3 border-t border-[color:var(--line)] space-y-2">
              {m.subscription_arr && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">Subscription ARR</div>
                  <div className="font-semibold text-sm">{m.subscription_arr}</div>
                </div>
              )}
              {m.first_year_revenue && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">Year-1 revenue (incl. setup)</div>
                  <div className="font-semibold text-sm">{m.first_year_revenue}</div>
                </div>
              )}
              {m.assumptions && (
                <div className="text-[11px] text-[color:var(--muted)] italic leading-snug">
                  {m.assumptions}
                </div>
              )}
              {/* Legacy fallback for cached rows that only have revenue_ceiling */}
              {!m.subscription_arr && !m.first_year_revenue && m.revenue_ceiling && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">Yr-1 ceiling</div>
                  <div className="font-semibold text-sm">{m.revenue_ceiling}</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SizingCard({ label, sizing, testid }) {
  if (!sizing) return null;
  // Legacy string fallback (older cached rows)
  const isLegacy = typeof sizing === "string";
  const value = isLegacy ? sizing : (sizing.value || "");
  const basis = isLegacy ? "estimate" : (sizing.basis || "estimate");
  const source = isLegacy ? "" : (sizing.source_or_method || "");
  // Derive confidence: prefer explicit `confidence` field, else map legacy basis.
  const confidence = isLegacy
    ? "ai_assessment"
    : (sizing.confidence || (basis === "cited" ? "measured" : "ai_assessment"));
  const sourceLabel = confidence === "measured" ? "Source:" : "Method:";
  return (
    <div className="border border-[color:var(--line)] p-3 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-mono-tight uppercase tracking-widest text-[color:var(--muted)]">{label}</div>
        <ConfidenceBadge confidence={confidence} testId={`${testid}-confidence`} />
      </div>
      <div className="font-display text-xl sm:text-2xl md:text-3xl leading-tight mt-1 break-words">
        {value || "—"}
      </div>
      {source && (
        <div className="text-[11px] text-[color:var(--muted)] leading-snug mt-2">
          <span className="font-mono-tight uppercase tracking-widest text-[9px] mr-1">{sourceLabel}</span>
          {source}
        </div>
      )}
    </div>
  );
}

function BusinessPlanSection({ plan }) {
  return (
    <div>
      <SectionHeader icon={FileText} label="Business Plan" sublabel="the pitch deck condensed" />
      <div className="mb-5 border-l-2 border-[color:var(--fg)] pl-4">
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">Executive summary</div>
        <p className="text-sm leading-relaxed mt-1">{plan.executive_summary}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        <div className="border border-[color:var(--line)] p-3">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">Problem</div>
          <p className="text-sm mt-1">{plan.problem}</p>
        </div>
        <div className="border border-[color:var(--line)] p-3">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)]">Solution</div>
          <p className="text-sm mt-1">{plan.solution}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        <SizingCard label="TAM" sizing={plan.tam} testid="sizing-tam" />
        <SizingCard label="SAM" sizing={plan.sam} testid="sizing-sam" />
        <SizingCard label="SOM (conservative)" sizing={plan.som} testid="sizing-som" />
      </div>
      {(plan.som_year1_scenario || plan.som_conservative_case) && (
        <div className="mb-5 border border-[color:var(--fg)] p-3 space-y-2" data-testid="som-scenarios">
          <div className="text-[10px] font-mono-tight uppercase tracking-widest text-[color:var(--muted)]">
            Year-1 revenue scenarios (reconciled with pricing above)
          </div>
          {plan.som_year1_scenario && (
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 text-sm">
              <span className="font-mono-tight uppercase tracking-widest text-[10px] text-[color:var(--accent)] flex-shrink-0">
                Aggressive:
              </span>
              <span className="leading-snug">{plan.som_year1_scenario}</span>
            </div>
          )}
          {plan.som_conservative_case && (
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 text-sm">
              <span className="font-mono-tight uppercase tracking-widest text-[10px] text-[color:var(--muted)] flex-shrink-0">
                Planning case:
              </span>
              <span className="leading-snug">{plan.som_conservative_case}</span>
            </div>
          )}
        </div>
      )}
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)] mb-2 flex items-center gap-2">
          <Target size={12} /> Go-to-Market
        </div>
        <p className="text-sm leading-relaxed">{plan.gtm_strategy}</p>
      </div>
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)] mb-2">Milestones</div>
        <div className="space-y-3">
          {plan.milestones.map((ms, i) => (
            <div key={`ms-${i}`} className="grid grid-cols-12 gap-3">
              <div className="col-span-3 md:col-span-2">
                <div className="font-mono-tight text-xs bg-[color:var(--fg)] text-[color:var(--bg)] inline-block px-2 py-1">
                  {ms.period}
                </div>
              </div>
              <ul className="col-span-9 md:col-span-10 space-y-1 text-sm">
                {ms.goals.map((g, j) => (
                  <li key={`g-${j}`} className="flex gap-2">
                    <CheckCircle2 size={12} className="mt-1 flex-shrink-0" />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--muted)] mb-2 flex items-center gap-2">
          <AlertTriangle size={12} className="text-[color:var(--hot)]" /> Key risks
        </div>
        <ul className="space-y-1.5 text-sm">
          {plan.key_risks.map((r, i) => (
            <li key={`r-${i}`} className="flex gap-2">
              <span className="text-[color:var(--hot)] mt-1">!</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function RealityCheckTier({ tier, items, testid }) {
  const cfg = {
    strong: {
      dot: "bg-[color:var(--accent)]",
      border: "border-[color:var(--accent)]",
      label: "Evidence supports",
      caption: "Backed by observed evidence or verifiable sources",
    },
    uncertain: {
      dot: "bg-amber-400",
      border: "border-amber-400/60",
      label: "Needs validation",
      caption: "Plausible but unverified — test with customers",
    },
    unproven: {
      dot: "bg-[color:var(--hot)]",
      border: "border-[color:var(--hot)]",
      label: "Not yet proven",
      caption: "Specific numbers the AI cannot yet substantiate",
    },
  }[tier];
  if (!items || items.length === 0) return null;
  return (
    <div className={`border ${cfg.border} p-3`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${cfg.dot} flex-shrink-0`} />
        <div className="font-mono-tight uppercase tracking-widest text-[10px]">{cfg.label}</div>
        <span className="text-[10px] font-mono-tight text-[color:var(--muted)]">· {items.length}</span>
      </div>
      <div className="text-[10px] text-[color:var(--muted)] italic mb-2 leading-snug">{cfg.caption}</div>
      <ul className="space-y-1.5 text-sm">
        {items.map((s, i) => (
          <li key={`${tier}-${i}`} className="flex gap-2 leading-snug">
            <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RealityCheckSection({ rc, onRegenerate, regenerating }) {
  if (!rc) return null;
  const hasAny =
    (rc.strong?.length || 0) +
    (rc.uncertain?.length || 0) +
    (rc.unproven?.length || 0) +
    (rc.validation_required?.length || 0);
  if (!hasAny) return null;
  return (
    <div data-testid="reality-check">
      <div className="flex items-start justify-between gap-2">
        <SectionHeader
          icon={Microscope}
          label="Reality Check"
          sublabel="what the evidence supports · what to prove"
        />
        {onRegenerate && (
          <button
            type="button"
            onClick={onRegenerate}
            disabled={regenerating}
            data-testid={APP.realityCheckRegenBtn}
            className="text-[10px] font-mono-tight uppercase tracking-widest border border-[color:var(--muted)] text-[color:var(--muted)] hover:border-[color:var(--fg)] hover:text-[color:var(--fg)] px-2.5 py-1.5 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            title="Re-grade this idea with a fresh AI pass"
          >
            {regenerating ? (
              <>
                <Loader2 size={11} className="animate-spin" /> Regrading…
              </>
            ) : (
              <>
                <RefreshCw size={11} /> Regrade
              </>
            )}
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <RealityCheckTier tier="strong" items={rc.strong} testid="reality-strong" />
        <RealityCheckTier tier="uncertain" items={rc.uncertain} testid="reality-uncertain" />
        <RealityCheckTier tier="unproven" items={rc.unproven} testid="reality-unproven" />
      </div>
      {rc.validation_required && rc.validation_required.length > 0 && (
        <div className="border-2 border-[color:var(--fg)] p-4" data-testid="validation-required">
          <div className="flex items-center gap-2 mb-2">
            <HelpCircle size={14} />
            <div className="font-mono-tight uppercase tracking-widest text-[10px]">
              Validation required
            </div>
          </div>
          <div className="text-[11px] text-[color:var(--muted)] italic mb-3 leading-snug">
            Complete these customer conversations before committing capital.
          </div>
          <ul className="space-y-2 text-sm">
            {rc.validation_required.map((v, i) => (
              <li key={`vr-${i}`} className="flex gap-2 leading-snug">
                <span className="font-mono-tight text-xs bg-[color:var(--fg)] text-[color:var(--bg)] px-1.5 py-0.5 flex-shrink-0 h-fit">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{v}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}



// Extracted body — reusable in the drawer (Sheet) and in inline read-only contexts (SharedIdea page).
function DeepDiveBody({ idea, dive, loading, error, onRegenerateReality, regenerating, onShare }) {
  return (
    <>
      <div className="border-b border-[color:var(--fg)] p-6 bg-[color:var(--bg)] text-left">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <Rocket size={16} />
            <span className="text-[10px] font-mono-tight uppercase tracking-widest">// deep dive</span>
          </div>
          {onShare && dive && !loading && (
            <button
              type="button"
              onClick={onShare}
              data-testid={APP.shareBtn}
              className="text-[10px] font-mono-tight uppercase tracking-widest border border-[color:var(--fg)] hover:bg-[color:var(--fg)] hover:text-[color:var(--bg)] px-2.5 py-1.5 flex items-center gap-1.5 transition-colors flex-shrink-0"
              title="Copy a public share link to this dossier"
            >
              <Share2 size={11} /> Share
            </button>
          )}
        </div>
        <div className="font-display text-3xl leading-tight tracking-tight">{idea?.title || "Deep Dive"}</div>
        <div className="text-sm text-[color:var(--muted)] italic mt-1">{idea?.tagline}</div>
        <div className="text-[10px] font-mono-tight uppercase tracking-widest text-[color:var(--muted)] mt-3 pt-3 border-t border-[color:var(--line)]">
          AI generates hypotheses. Here's what you still need to prove.
        </div>
      </div>

      <div className="p-6">
        {loading && (
          <div data-testid={APP.deepDiveLoading} className="flex flex-col items-center py-24">
            <Loader2 size={32} className="animate-spin" />
            <div className="font-display text-2xl mt-6">Building the dossier…</div>
            <div className="text-sm text-[color:var(--muted)] mt-2 font-mono-tight">
              // market analysis · costs · monetization · plan
            </div>
            <div className="text-xs text-[color:var(--muted)] mt-4">~20-30s</div>
          </div>
        )}

        {error && !loading && (
          <div className="border border-[color:var(--hot)] p-4 text-sm">
            <div className="font-semibold text-[color:var(--hot)]">Deep dive failed</div>
            <div className="mt-1">{error}</div>
          </div>
        )}

        {dive && !loading && (
          <div className="space-y-10">
            <MarketDemandSection md={dive.market_demand} />
            <RealityCheckSection
              rc={dive.reality_check}
              onRegenerate={onRegenerateReality}
              regenerating={regenerating}
            />
            <CostSection cost={dive.cost_breakdown} />
            <MonetizationSection models={dive.monetization_models} />
            <BusinessPlanSection plan={dive.business_plan} />
          </div>
        )}
      </div>
    </>
  );
}


export default function DeepDiveDrawer({
  open, onOpenChange, idea, dive, loading, error,
  inline = false, onRegenerateReality, regenerating = false, onShare,
}) {
  if (inline) {
    // Inline mode — no Sheet overlay. Used by the public SharedIdea page.
    // Read-only: no share/regen actions exposed.
    return (
      <div data-testid={APP.deepDiveDrawer} className="bg-[color:var(--bg)] text-[color:var(--fg)]">
        <DeepDiveBody
          idea={idea}
          dive={dive}
          loading={loading}
          error={error}
        />
      </div>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid={APP.deepDiveDrawer}
        className="w-full sm:max-w-[720px] overflow-y-auto bg-[color:var(--bg)] text-[color:var(--fg)] border-l-2 border-[color:var(--fg)] p-0"
      >
        {/* Screen-reader-only title (SheetContent requires accessible label) */}
        <SheetHeader className="sr-only">
          <SheetTitle>{idea?.title || "Deep Dive"}</SheetTitle>
          <SheetDescription>{idea?.tagline || ""}</SheetDescription>
        </SheetHeader>
        <DeepDiveBody
          idea={idea}
          dive={dive}
          loading={loading}
          error={error}
          onRegenerateReality={onRegenerateReality}
          regenerating={regenerating}
          onShare={onShare}
        />
      </SheetContent>
    </Sheet>
  );
}
